import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import jobGradeApi, { F903 } from '../../../services/jobGradeApi';

type Mode = 'browse' | 'add' | 'edit';

export function useJobGradeForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F903[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [f903_01, setF90301] = useState('');
  const [f903_11, setF90311] = useState('');
  const [f903_91, setF90391] = useState('');
  const [f903_92, setF90392] = useState<string | null>(null);
  const [f903_93, setF90393] = useState('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jobGradeApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = useCallback((rec: F903) => {
    setF90301(rec.f903_01 || '');
    setF90311(rec.f903_11 || '');
    setF90391(rec.f903_91 || '');
    setF90392(rec.f903_92);
    setF90393(rec.f903_93 || '');
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const startAdd = useCallback(() => {
    setMode('add');
    setF90301('');
    setF90311('');
    setF90391('');
    setF90392(null);
    setF90393('');
  }, []);

  const startEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  const buildPayload = useCallback((): Partial<F903> => ({
    f903_01: f903_01.trim(),
    f903_11: f903_11.trim(),
    f903_91: f903_91,
    f903_93: f903_93,
  }), [f903_01, f903_11, f903_91, f903_93]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildPayload();
      if (mode === 'add') {
        await jobGradeApi.create(payload);
        message.success('新增成功');
      } else {
        await jobGradeApi.update(f903_01, payload);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, f903_01, buildPayload, loadRecords]);

  const doDelete = useCallback(async () => {
    if (!f903_01) return;
    setLoading(true);
    try {
      await jobGradeApi.delete(f903_01);
      message.success('刪除成功');
      await loadRecords();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [f903_01, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) => r.f903_01.toUpperCase().startsWith(keyword.toUpperCase()));
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, records, currentIdx,
    f903_01, f903_11, f903_91, f903_92, f903_93,
    setF90301, setF90311,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
